import { computed } from "vue";
import { storeToRefs } from "pinia";
import type {
  SlideBboxArray,
  SlideRegionDict,
  SlideRegionEntry,
} from "@/components/RegionBasedAnnotation/components/SlideCanvas.types";
import type { Annotation, AnnotationSchemaPayload } from "@/components/RegionBasedAnnotation/types/annotation";
import { useRBCSlideStore } from "@/store/modules/RBCregion";
import { createBoxId } from "@/components/RegionBasedAnnotation/utils/slideGeometry";

export type LabelTogglePayload = {
  label: string;
  checked: boolean;
};

export function useRegionBasedAnnotation() {
  const slideStore = useRBCSlideStore();
  const {
    images,
    labelFilter,
    currentSelectedSlide,
    selectedLabel,
  } = storeToRefs(slideStore);

  const currentSlide = computed(() => {
    if (!currentSelectedSlide.value) return null;
    return images.value[currentSelectedSlide.value] ?? null;
  });

  const labelFilterImages = computed<AnnotationSchemaPayload>(() => {
    if (!currentSlide.value || !currentSelectedSlide.value) return {};
    return { [currentSelectedSlide.value]: currentSlide.value };
  });

  const canvasRegions = computed<SlideRegionDict | null>(() => {
    const slide = currentSlide.value;
    if (!slide) return null;
    return annotationsToRegions(slide.annotations);
  });

  const backgroundImage = computed(() => currentSlide.value?.image_url ?? undefined);

  function handleSelectSlide(slideId: string) {
    if (!slideId || slideId === currentSelectedSlide.value) return;
    slideStore.setCurrentSlide(slideId);
  }

  function handleLabelToggle(payload: LabelTogglePayload) {
    slideStore.setLabelFilterValue(payload.label, payload.checked);
  }

  function handleRegionsUpdate(next: SlideRegionDict) {
    if (!currentSelectedSlide.value) return;
    const annotations = regionsToAnnotations(next);
    slideStore.updateAnnotations(currentSelectedSlide.value, annotations);
  }

  function handleDeleteSlide(slideId: string) {
    slideStore.deleteSlide(slideId);
  }

  function handleRestoreSlide(slideId: string) {
    slideStore.restoreSlide(slideId);
  }

  return {
    images,
    labelFilter,
    selectedLabel,
    currentSelectedSlide,
    labelFilterImages,
    canvasRegions,
    backgroundImage,
    handleSelectSlide,
    handleLabelToggle,
    handleRegionsUpdate,
    handleDeleteSlide,
    handleRestoreSlide,
  };
}

function annotationsToRegions(annotations: Annotation[]): SlideRegionDict {
  return annotations.reduce<SlideRegionDict>((acc, annotation) => {
    if (!annotation.label) return acc;
    const key = annotation.label;
    const bucket = acc[key] ?? [];
    const entry: SlideRegionEntry = {
      id: createBoxId(),
      bounds: cloneBounds(annotation.bbox),
      rotation: annotation.rotation ?? 0,
    };
    bucket.push(entry);
    acc[key] = bucket;
    return acc;
  }, {});
}

function regionsToAnnotations(dict: SlideRegionDict): Annotation[] {
  return Object.entries(dict).flatMap(([label, entries]) => {
    return entries.map((entry) => ({
      label,
      bbox: cloneBounds(entry.bounds),
      rotation: entry.rotation ?? 0,
    }));
  });
}

function cloneBounds(bounds: SlideBboxArray): SlideBboxArray {
  return [bounds[0], bounds[1], bounds[2], bounds[3]];
}
